import classNames from 'classnames';

import Button from '@/components/shared/Button/Button';
import { ExploreCollection } from '@addmile/backend-client';

import { COLLECTION_PERSONALIZATIONS, PERSONALIZATION_COLORS } from '../../const';
import { withCollectionDetails } from '../ContentCard/withCollectionDetails';
import ContentStatusComponent from '../ContentStatus/ContentStatus';
import LanguageFlag from '../LanguageFlag/LanguageFlag';

interface CollectionCardProps {
  collection: ExploreCollection;
  onEdit: (slug: string) => void;
  onDelete: (slug: string) => void;
  blocksCount?: number;
  // The card loads one page of blocks, so a full page means the count is a lower bound.
  hasMoreBlocks?: boolean;
  isLoadingBlocksCount?: boolean;
  onViewBlocks?: (slug: string) => void;
}

const CollectionCardBase = ({
  collection,
  onEdit,
  onDelete,
  blocksCount,
  hasMoreBlocks,
  isLoadingBlocksCount,
  onViewBlocks,
}: CollectionCardProps) => {
  const personalizationImage = COLLECTION_PERSONALIZATIONS[collection.personalization];
  const personalizations = [collection.personalization];
  const handleCardClick = () => {
    onViewBlocks?.(collection.slug);
  };

  return (
    <div
      className={classNames('flex items-center justify-between rounded-2xl bg-white-100 p-5', {
        'cursor-pointer': !!onViewBlocks,
      })}
      onClick={handleCardClick}
    >
      <div className="flex gap-3 justify-start items-start h-full">
        {personalizationImage && (
          <img
            src={personalizationImage}
            alt={collection.title}
            className="rounded-full w-20 h-20 m-auto"
          />
        )}
        <div className="flex flex-col gap-6 justify-between h-full">
          <div className="flex gap-2 items-center">
            <p className="text-2xl font-bold text-neutral-90">{collection.title}</p>
            {!isLoadingBlocksCount && blocksCount !== undefined && (
              <span className="text-medium px-2 py-1 rounded-lg w-fit bg-neutral-10 text-neutral-40">
                {`${blocksCount}${hasMoreBlocks ? '+' : ''} items`}
              </span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {personalizations.map((personalization) => (
              <span
                key={personalization}
                className={classNames(
                  'text-medium px-2 py-1 rounded-lg w-fit',
                  PERSONALIZATION_COLORS[personalization] || 'bg-neutral-10',
                )}
              >
                {personalization}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <LanguageFlag language={collection.language} />
        <ContentStatusComponent status={collection.status} />
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          className="flex items-center gap-3"
        >
          <Button
            isIcon
            className="!border-neutral-20 ml-2"
            variant="transparent"
            prefixIcon={<img src="/icons/pencil.svg" alt="edit" />}
            onClick={() => {
              onEdit(collection.slug);
            }}
          />

          <Button
            isIcon
            className="border-none"
            variant="transparent"
            prefixIcon={<img src="/icons/trash.svg" alt="delete" />}
            onClick={() => {
              onDelete(collection.slug);
            }}
          />
        </div>
      </div>
    </div>
  );
};

const CollectionCard = withCollectionDetails(CollectionCardBase);

export default CollectionCard;
